import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const SEVERITY_CONFIG = {
  critical: { bg: "bg-red-50", border: "border-red-200", badge: "bg-red-100 text-red-800", icon: "text-red-600", label: "Critical" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-800", icon: "text-amber-600", label: "Warning" },
  opportunity: { bg: "bg-emerald-50", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-800", icon: "text-emerald-600", label: "Opportunity" },
  info: { bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-100 text-blue-800", icon: "text-blue-600", label: "Info" },
};

const TYPE_ICONS = {
  tax: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  cash: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
  wealth: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  expense: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
};

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(n || 0);

const InsightCard = ({ insight, businessId, onUpdate, compact = false }) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const config = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.info;
  const icon = TYPE_ICONS[insight.type] || TYPE_ICONS.cash;

  const handleActivate = async () => {
    setLoading(true);
    try {
      await api.post(`/insights/${businessId}/activate`, { rule_id: insight.rule_id });
      toast.success("Step activated! Track your progress below.");
      if (insight.action_type === "navigate" && insight.action_data?.route) {
        navigate(insight.action_data.route);
      }
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to activate");
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await api.post(`/insights/${businessId}/complete`, { rule_id: insight.rule_id });
      toast.success("Step completed!");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to complete");
    } finally {
      setLoading(false);
    }
  };

  const handleDefer = async (days = 7) => {
    setLoading(true);
    try {
      await api.post(`/insights/${businessId}/defer`, { rule_id: insight.rule_id, days });
      toast.success(`Reminder set for ${days} days from now.`);
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to defer");
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  if (compact) {
    return (
      <div
        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${config.bg} ${config.border}`}
        onClick={() => setShowModal(true)}
        data-testid={`insight-compact-${insight.rule_id}`}
      >
        <div className={`flex-shrink-0 ${config.icon}`}>{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-stone-900 truncate">{insight.title}</p>
          {insight.financial_impact > 0 && (
            <p className="text-xs text-stone-500">Impact: {fmt(insight.financial_impact)}</p>
          )}
        </div>
        <Badge className={`text-[10px] border-0 ${config.badge}`}>{config.label}</Badge>

        {/* Detail Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg" onClick={(e) => e.stopPropagation()}>
            <InsightModalContent
              insight={insight}
              config={config}
              icon={icon}
              onActivate={handleActivate}
              onComplete={handleComplete}
              onDefer={handleDefer}
              loading={loading}
            />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <>
      <Card className={`overflow-hidden border ${config.border} transition-all hover:shadow-md`} data-testid={`insight-card-${insight.rule_id}`}>
        <div className={`h-1 ${insight.severity === 'critical' ? 'bg-red-500' : insight.severity === 'warning' ? 'bg-amber-500' : insight.severity === 'opportunity' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
        <CardContent className={`pt-4 pb-4 ${config.bg}`}>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${config.bg} ${config.icon}`} style={{ border: `1px solid currentColor`, opacity: 0.8 }}>
              {icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-sm text-stone-900">{insight.title}</h4>
                <Badge className={`text-[10px] border-0 ${config.badge}`}>{config.label}</Badge>
              </div>
              <p className="text-xs text-stone-600 mb-3 line-clamp-2">{insight.description}</p>

              {insight.financial_impact > 0 && (
                <div className="flex items-center gap-1 mb-3">
                  <span className="text-xs text-stone-500">Financial impact:</span>
                  <span className="text-sm font-bold text-[#1e3a5f]">{fmt(insight.financial_impact)}</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                {insight.status === "in_progress" ? (
                  <Button size="sm" onClick={handleComplete} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-xs rounded-full" data-testid={`insight-complete-${insight.rule_id}`}>
                    Mark Complete
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => setShowModal(true)} disabled={loading} className="bg-[#1e3a5f] hover:bg-[#2d5a8e] text-xs rounded-full" data-testid={`insight-activate-${insight.rule_id}`}>
                    Activate This Step
                  </Button>
                )}
                {insight.status === "in_progress" && (
                  <Badge variant="outline" className="text-xs text-[#1e3a5f] border-[#1e3a5f]/30">In Progress</Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <InsightModalContent
            insight={insight}
            config={config}
            icon={icon}
            onActivate={handleActivate}
            onComplete={handleComplete}
            onDefer={handleDefer}
            loading={loading}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const InsightModalContent = ({ insight, config, icon, onActivate, onComplete, onDefer, loading }) => (
  <>
    <DialogHeader>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bg} ${config.icon}`}>
          {icon}
        </div>
        <div>
          <DialogTitle className="text-lg">{insight.title}</DialogTitle>
          <Badge className={`text-[10px] border-0 mt-1 ${config.badge}`}>{config.label}</Badge>
        </div>
      </div>
      <DialogDescription className="text-sm text-stone-600 mt-2">
        {insight.description}
      </DialogDescription>
    </DialogHeader>

    <div className="space-y-4 py-2">
      {insight.financial_impact > 0 && (
        <div className="p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/10">
          <p className="text-xs text-[#1e3a5f] font-medium mb-1">Financial Impact</p>
          <p className="text-2xl font-bold text-[#1e3a5f]">{fmt(insight.financial_impact)}</p>
        </div>
      )}

      <div className="p-4 rounded-lg bg-stone-50">
        <p className="text-xs font-semibold text-stone-700 mb-1">Why This Matters</p>
        <p className="text-sm text-stone-600">{insight.why_it_matters}</p>
      </div>

      <div className="space-y-2 pt-2">
        <Button
          className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e] rounded-full h-11"
          onClick={onActivate}
          disabled={loading}
          data-testid={`insight-modal-activate-${insight.rule_id}`}
        >
          {insight.action_label || "Activate This Step"}
        </Button>

        {insight.status === "in_progress" && (
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full h-11"
            onClick={onComplete}
            disabled={loading}
            data-testid={`insight-modal-complete-${insight.rule_id}`}
          >
            Mark as Complete
          </Button>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="rounded-full text-xs" onClick={() => onDefer(7)} disabled={loading}>
            Remind in 7 Days
          </Button>
          <Button variant="outline" className="rounded-full text-xs" onClick={() => onDefer(30)} disabled={loading}>
            Remind in 30 Days
          </Button>
        </div>
      </div>
    </div>
  </>
);

export default InsightCard;
